import { api, setCatalogLink, setCatalogVideo, trpc } from '@tg/app-core';
import { linkLabel, videoLink, type VideoLink, type VideoRef } from '@tg/domain';
import { useRef, useState, type ReactNode } from 'react';
import { Modal } from './Modal';
import { RopeAnim } from '../constructor/AiWait';
import { ICON } from '../constructor/icons';
import { Svg } from '../constructor/Svg';

/* Видео тренера (VID-1, VID-2): файл с телефона/компьютера уходит из браузера прямо в Kinescope
   (адрес загрузки выдаёт сервер — videoInit), наружу — ссылка на плеер (VideoRef). */

const MAX = 4 * 1024 ** 3;

function send(endpoint: string, file: File, onPct: (p: number) => void) {
  return new Promise<void>((ok, fail) => {
    const x = new XMLHttpRequest();
    x.open('POST', endpoint);
    x.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    x.upload.onprogress = (e) => e.lengthComputable && onPct(Math.round((e.loaded / e.total) * 100));
    x.onload = () => (x.status >= 200 && x.status < 300 ? ok() : fail(new Error('upload ' + x.status)));
    x.onerror = () => fail(new Error('Нет связи — видео не загрузилось'));
    x.send(file);
  });
}

export async function uploadVideo(file: File, title: string, onPct: (p: number) => void): Promise<VideoRef> {
  if (!file.type.startsWith('video/') && !/\.(mov|mp4|m4v|avi|mkv|webm|3gp)$/i.test(file.name)) throw new Error('Нужен видеофайл');
  if (file.size > MAX) throw new Error('Файл больше 4 ГБ — обрежьте или сожмите видео');
  const r = await api.trainer.videoInit.mutate({ title, filename: file.name, size: file.size });
  try {
    await send(r.endpoint, file, onPct);
  } catch (e) {
    api.trainer.videoUploaded.mutate({ id: r.id, ok: false }).catch(() => {});
    throw e instanceof Error && /^upload/.test(e.message) ? new Error('Видеохостинг не принял файл — попробуйте ещё раз') : e;
  }
  await api.trainer.videoUploaded.mutate({ id: r.id, ok: true });
  return r.video;
}

/** Выбор файла и загрузка: pick() открывает выбор, пока грузится — pct (0–100), иначе null */
export function useVideoUpload(onDone: (v: VideoRef) => void, onError: (msg: string) => void, title = '') {
  const inp = useRef<HTMLInputElement>(null);
  const [pct, setPct] = useState<number | null>(null);
  const run = async (f: File | null | undefined) => {
    if (!f || pct != null) return;
    setPct(0);
    try {
      onDone(await uploadVideo(f, title, setPct));
    } catch (e) {
      onError(e instanceof Error && e.message && !/fetch/i.test(e.message) ? e.message : 'Не удалось загрузить видео — попробуйте ещё раз');
    } finally {
      setPct(null);
      if (inp.current) inp.current.value = '';
    }
  };
  const input = <input ref={inp} type="file" accept="video/*" hidden onChange={(e) => run(e.target.files?.[0])} />;
  return { pick: () => inp.current?.click(), input, pct, busy: pct != null };
}

/** Видео тренера к упражнению базы (VID-2): сохраняется за упражнением — только у этого тренера —
    и само подставляется во все его тренировки. null — убрать из упражнения. */
export function useExVideo() {
  const utils = trpc.useUtils();
  const m = trpc.trainer.setExVideo.useMutation();
  return async (exId: string, video: VideoRef | null) => {
    const prev = utils.catalog.exercises.getData()?.find((x) => x.id === exId)?.video ?? null;
    setCatalogVideo(utils, exId, video);
    try {
      await m.mutateAsync({ exId, video });
    } catch (e) {
      setCatalogVideo(utils, exId, prev as VideoRef | null);
      throw e;
    }
  };
}

/** Плеер Kinescope; пока видео обрабатывается — заглушка со статусом */
export function VideoPlayer({ video }: { video: VideoRef }) {
  const st = trpc.trainer.videoStatus.useQuery({ id: video.id }, { refetchInterval: (q) => (q.state.data?.status === 'processing' || q.state.data?.status === 'uploading' ? 5000 : false) });
  const s = st.data?.status;
  return (
    <div className="vplay">
      {s === 'ready' || (s == null && st.isError) ? (
        <iframe src={video.embed} allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen title={video.title || 'Видео'} />
      ) : (
        <div className="vwait">
          {s === 'error' ? (
            <b>Видео не обработалось — загрузите файл ещё раз</b>
          ) : (
            <>
              <RopeAnim size={110} />
              <b>Видео обрабатывается{st.data?.progress ? ` · ${st.data.progress}%` : ''}</b>
              <s>Обычно это минута-две, идёт на сервере — страницу можно закрыть. Клиенту видео уже прикреплено: он увидит его, как только обработка закончится.</s>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Окно видео: плеер и действия */
export function VideoModal({ video, title, onClose, actions }: { video: VideoRef; title: string; onClose: () => void; actions?: ReactNode }) {
  return (
    <Modal title={title} wide onClose={onClose} foot={actions}>
      <VideoPlayer video={video} />
    </Modal>
  );
}

/* ─── Ссылка на ролик на видеохостинге (VID-3): YouTube, Rutube, VK Видео, Instagram… — клиент открывает её там ─── */

/** Иконка «открыть снаружи» (своя: в иконках прототипа такой нет) */
export const EXT_SVG =
  '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 2.5h4v4M13.5 2.5 7.5 8.5"/><path d="M12 9.5V12a1.5 1.5 0 0 1-1.5 1.5h-6A1.5 1.5 0 0 1 3 12V6a1.5 1.5 0 0 1 1.5-1.5H7"/></svg>';

/** Ссылка тренера к упражнению базы: как видео — своя у тренера, подставляется во все его тренировки. null — убрать */
export function useExLink() {
  const utils = trpc.useUtils();
  const m = trpc.trainer.setExLink.useMutation();
  return async (exId: string, url: string | null) => {
    const prev = utils.catalog.exercises.getData()?.find((x) => x.id === exId)?.link ?? null;
    const next = url == null ? null : videoLink(url);
    setCatalogLink(utils, exId, next);
    try {
      const r = await m.mutateAsync({ exId, url });
      setCatalogLink(utils, exId, r.link);
    } catch (e) {
      setCatalogLink(utils, exId, prev as VideoLink | null);
      throw e;
    }
  };
}

/** Открыть ссылку на хостинге — в новой вкладке */
export const openLink = (l: VideoLink) => window.open(l.url, '_blank', 'noopener');

/** Окно «Видео к упражнению»: вставить ссылку на ролик и/или загрузить свой файл.
    Можно и то и другое — клиент увидит оба (и демонстрацию из базы, если она есть). */
export function AddVideoModal({
  title,
  hint,
  link,
  upload,
  onLink,
  onClose,
}: {
  title: string;
  hint?: string;
  /** false — ссылка уже есть, поле не показываем */
  link: boolean;
  /** Загрузить файл (нет — видеохостинг не подключён или видео уже есть) */
  upload?: () => void;
  onLink: (url: string) => Promise<void> | void;
  onClose: () => void;
}) {
  const [v, setV] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const l = videoLink(v);
  const save = async () => {
    if (!l) return setErr('Это не ссылка — скопируйте адрес ролика из браузера или из «Поделиться» в приложении');
    setBusy(true);
    try {
      await onLink(l.url);
      onClose();
    } catch (e) {
      setErr(e instanceof Error && e.message ? e.message : 'Не сохранилось — попробуйте ещё раз');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={title} onClose={onClose}>
      <div className="vadd">
        {/* Сначала — загрузить своё видео (главное действие), ниже — ссылка на ролик */}
        {upload && (
          <button
            className="btn vup"
            onClick={() => {
              onClose();
              upload();
            }}
          >
            <Svg src={ICON.video} /> Загрузить своё видео
          </button>
        )}
        {link && upload && <div className="vor">или ссылка на ролик</div>}
        {link && (
          <>
            {!upload && <b>Ссылка на ролик</b>}
            <div className={'vurl' + (err ? ' bad' : '')}>
              <Svg src={EXT_SVG} />
              <input
                autoFocus={!upload}
                value={v}
                placeholder="Вставьте ссылку на ролик"
                inputMode="url"
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => {
                  setV(e.target.value);
                  setErr('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && save()}
              />
              <button className="btn gh sm" disabled={!v.trim() || busy} onClick={save}>
                Добавить
              </button>
            </div>
            <s className={err ? 'err' : ''}>{err || (l ? 'Клиент увидит кнопку «' + linkLabel(l) + '» — ролик откроется там' : 'Подойдёт ссылка с любого видеохостинга — клиент откроет ролик там')}</s>
          </>
        )}
        {hint && <s className="vhint">{hint}</s>}
      </div>
    </Modal>
  );
}
